<script setup lang="ts">
import type { Form, FormErrorEvent, FormSubmitEvent } from '#ui/types';
import { adaptFormDataToDto } from '@lib/shared';
import { computed } from 'vue';
import { z } from 'zod';

import {
    type ConfirmOnboardingSessionCompanyBodyDto,
    type BackendErrorEntity,
    type ErrorType,
    useApiConfirmOnboardingSessionCompany,
    useApiGetSelfOnboardingSessionCompany,
} from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';

// EVENTS
const emit = defineEmits(['success']);

// COMPOSABLES
const { logError, logInfo } = useLogs();
const { notifyBackendError, notifyGenericError } = useNotification();
const { tt } = useTypedI18n();
const { getFormErrors } = useBackendErrors();
const { data: session } = useAuthUtils();
const { data: onboardingSession, suspense: suspenseOnboardingSession, isError: isErrorOnboardingSession } = useApiGetSelfOnboardingSessionCompany();
const { mutateAsync, isPending } = useApiConfirmOnboardingSessionCompany();
const countryCode = computed(() => onboardingSession.value?.countryCode ?? undefined);
const provinces = useProvinces(countryCode);

await suspenseOnboardingSession();

// DATA
const isProvinceRequired = computed<boolean>(() => provinces.value.length > 0);
const formSchema = z
    .object({
        businessName: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.businessName') })),
        address: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.address') })),
        city: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.city') })),
        province: z
            .string()
            .refine(province => !isProvinceRequired.value || !!province, `${tt('validation.msg.required', { field: tt('signup.fields.province') })}`),
        postCode: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.postCode') })),
        pec: z.email(tt('validation.msg.email')).or(z.literal('')),
        sdi: z
            .string()
            .regex(/^[a-zA-Z0-9]+$/g, { message: tt('validation.msg.characters') })
            .length(7, tt('validation.msg.length', { count: 7 }))
            .or(z.literal('')),
    })
    .superRefine(({ sdi, pec }, ctx) => {
        if (!requiresSdiOrPec.value || sdi || pec) return;

        ctx.addIssue({
            code: 'custom',
            message: tt('signup.validations.requiredSDIOrPec'),
            path: ['sdi'],
        });
        ctx.addIssue({
            code: 'custom',
            message: tt('signup.validations.requiredSDIOrPec'),
            path: ['pec'],
        });
    });
type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;
const { address, businessName, city, pec, postCode, province: provinceCode, sdi } = onboardingSession.value || {};
const province = provinces.value.find(province => province.code === provinceCode)?.name;
const formData = reactive<Partial<FormData>>({
    businessName: businessName ?? '',
    address: address ?? '',
    city: city ?? '',
    province: province ?? '',
    postCode: postCode ?? '',
    pec: pec ?? '',
    sdi: sdi ?? '',
});
const form = useTemplateRef<Form<FormSchema>>('form');

const requiresSdiOrPec = computed<boolean>(() => {
    const countryRequiringFields = 'IT';

    return countryCode.value === countryRequiringFields;
});

// HANDLERS
const onSubmit = async (event: FormSubmitEvent<FormData>): Promise<void> => {
    const { data } = event;

    const _countryCode = countryCode.value;

    if (!_countryCode) {
        notifyGenericError();
        logError('Error during signup onboarding company step: country code undefined', {
            userId: session.value?.user?.id,
            companyId: session.value?.user?.companyId,
        });

        return;
    }

    const body: ConfirmOnboardingSessionCompanyBodyDto = {
        ...data,
        countryCode: _countryCode,
    };

    mutateAsync({ data: adaptFormDataToDto(body) })
        .then(() => {
            emit('success');
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error during signup onboarding company step', {
                apiError: apiError.data,
                userId: session.value?.user?.id,
                formData: data,
            });

            if (!apiError.data?.error) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({
                error,
            });

            const formErrors = getFormErrors<ConfirmOnboardingSessionCompanyBodyDto, FormData>(apiError, {
                address: 'address',
                businessName: 'businessName',
                city: 'city',
                pec: 'pec',
                postCode: 'postCode',
                province: 'province',
                sdi: 'sdi',
            });
            form.value?.setErrors(formErrors);
            scrollToFormError();
        });
};

const onError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before signup onboarding company step', {
        formErrors: errorEvent.errors,
        formData,
    });

    if (!errorEvent.errors.length) return;

    scrollToFormError();
};

// WATCHERS
watch(isErrorOnboardingSession, () => {
    if (isErrorOnboardingSession.value) {
        showError({ status: 500 });
    }
});

// LIFECYCLE
onMounted(() => {
    form?.value?.validate({ silent: true });
});
</script>

<template>
    <UForm ref="form" class="flex flex-col gap-4" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onError">
        <p class="text-center">{{ tt('signup.onboarding.company.subtitle') }}</p>

        <UFormField :label="tt('signup.fields.businessName')" name="businessName" required>
            <UInput v-model.trim="formData.businessName" :disabled="isPending" :autocomplete="AUTOCOMPLETE_OFF" />
        </UFormField>

        <UFormField :label="tt('signup.fields.address')" name="address" required>
            <UInput v-model.trim="formData.address" :disabled="isPending" :autocomplete="AUTOCOMPLETE_OFF" />
        </UFormField>

        <UFormField :label="tt('signup.fields.city')" class="w-full" name="city" required>
            <UInput v-model.trim="formData.city" :disabled="isPending" :autocomplete="AUTOCOMPLETE_OFF" />
        </UFormField>

        <div class="flex flex-col gap-4 md:flex-row">
            <UFormField :label="tt('signup.fields.postCode')" name="postCode" required>
                <UInput v-model.trim="formData.postCode" :disabled="isPending" :autocomplete="AUTOCOMPLETE_OFF" />
            </UFormField>

            <UFormField :label="tt('signup.fields.province')" class="w-full" name="province" :required="isProvinceRequired">
                <SignupProvince v-model.trim="formData.province" :disabled="isPending" :autocomplete="AUTOCOMPLETE_OFF" :provinces="provinces" />
            </UFormField>
        </div>

        <div v-if="requiresSdiOrPec" class="flex gap-4 md:flex-col">
            <UFormField :label="tt('signup.fields.pec')" class="flex-1" name="pec" :required="!formData.sdi">
                <UInput
                    v-model.trim="formData.pec"
                    :disabled="isPending"
                    :autocomplete="AUTOCOMPLETE_OFF"
                    @blur="form?.validate({ name: ['pec', 'sdi'], silent: true })"
                />
            </UFormField>

            <UFormField :label="tt('signup.fields.sdi')" class="flex-1" name="sdi" :required="!formData.pec">
                <UInput
                    v-model.trim="formData.sdi"
                    :disabled="isPending"
                    :autocomplete="AUTOCOMPLETE_OFF"
                    @blur="form?.validate({ name: ['pec', 'sdi'], silent: true })"
                />
            </UFormField>
        </div>

        <UButton :label="tt('signup.onboarding.confirm')" type="submit" size="xl" class="mt-4" :loading="isPending" block />
    </UForm>
</template>
